<script lang="ts">
const DEFAULT_INTEGER_PRECISION = 0;
const DEFAULT_DECIMAL_MAX_PRECISION = 16;

function useLocaleString(locale?: string, options?: MaybeRefOrGetter<Intl.NumberFormatOptions>) {
  const localeNumberFormat = computed(() => Intl.NumberFormat(locale, toValue(options)));
  const thousandSeparator = computed(() =>
    localeNumberFormat.value.format(1111).replace(/^\D*1(\D).*$/, '$1')
  );
  const decimalSeparator = computed(() =>
    (localeNumberFormat.value.resolvedOptions().maximumFractionDigits ?? 0) === 0
      ? // just a stub, never happens
        thousandSeparator.value === '.'
        ? ','
        : '.'
      : localeNumberFormat.value.format(1.1).replace(/^\D*1(\D).*$/, '$1')
  );

  function locale2str(localeValue: string) {
    return localeValue
      .replaceAll(thousandSeparator.value, '')
      .replaceAll(decimalSeparator.value, '.') as Intl.StringNumericLiteral;
  }

  function str2locale(strValue: string) {
    let strNumericLiteral = strValue.replace(
      /^[^-.0-9]*(-?)[^0-9.]*([0-9]*\.?)[^0-9]*([0-9]*)(.*)$/,
      '$1$2$3'
    );
    strNumericLiteral = strNumericLiteral.slice(
      0,
      (strNumericLiteral.startsWith('0.') ? 2 : +strNumericLiteral.includes('.')) +
        DEFAULT_DECIMAL_MAX_PRECISION
    );
    const result = localeNumberFormat.value.format(strNumericLiteral as Intl.StringNumericLiteral);
    return !result.includes('NaN') ? result : null;
  }

  const localizedValue = shallowRef<null | string>(null);
  const stringValue = computed<null | string>({
    get() {
      return localizedValue.value ? locale2str(localizedValue.value) : null;
    },
    set(v) {
      localizedValue.value = v ? str2locale(v) : null;
    },
  });

  const inputBackup = shallowRef('');
  const isTogglingDomSync = shallowRef(false);
  const formattedInputValue = computed({
    get() {
      return localizedValue.value ?? inputBackup.value;
    },
    set(v) {
      inputBackup.value = v;
      const localizedBefore = localizedValue.value;
      stringValue.value = locale2str(v);
      if (localizedValue.value === localizedBefore && localizedBefore !== null) {
        // The numeric value didn't change (e.g. "1..0000" → "1.0000"), but the DOM
        // is out of sync with VTextField's lazyValue. Toggle through null so
        // VTextField's value-prop watcher fires and resets lazyValue on the next tick.
        isTogglingDomSync.value = true;
        localizedValue.value = null;
        nextTick(() => {
          localizedValue.value = localizedBefore;
          isTogglingDomSync.value = false;
        });
      }
    },
  });

  return {
    stringValue,
    formattedInputValue,
    isTogglingDomSync,
    dropBackup() {
      inputBackup.value = '';
    },
    thousandSeparator,
    decimalSeparator,
  };
}

/**
 * Count characters that are not the thousands separator up to a given position.
 * This gives a cursor position in terms of "meaningful" characters (digits, decimal point, minus).
 */
function countMeaningfulChars(value: string, upTo: number, separator: string): number {
  let count = 0;
  for (let i = 0; i < upTo && i < value.length; i++) {
    if (value[i] !== separator) count++;
  }
  return count;
}

/**
 * Find the position in a formatted string that has exactly `n` meaningful characters before it.
 * This is the inverse of countMeaningfulChars — it maps a "meaningful char count" back to a
 * cursor offset in a potentially differently-formatted string.
 */
function findPositionAfterNMeaningful(value: string, n: number, separator: string): number {
  if (n <= 0) return 0;
  let counted = 0;
  for (let i = 0; i < value.length; i++) {
    if (value[i] !== separator) {
      counted++;
      if (counted === n) return i + 1;
    }
  }
  return value.length;
}
</script>

<script
  setup
  lang="ts"
  generic="
    T extends Type extends 'integer' ? number : Decimal,
    Type extends 'integer' | 'decimal' = 'integer'
  "
>
import { computed, nextTick, onBeforeMount, shallowRef, watch } from 'vue';
import Decimal from 'decimal.js';
import { MaybeRefOrGetter, toValue } from '@/utils/vue';

const props = withDefaults(
  defineProps<{
    value: T | null;
    type?: Type;
    precision?:
      | number
      | {
          min?: number | undefined;
          max: number;
        };
    rounding?: Decimal.Rounding;
    min?: number;
    max?: number;
    step?: number;
    label?: string;
    placeholder?: string;
    hint?: string;
    prefix?: string;
    suffix?: string;
    autofocus?: boolean;
    clearable?: boolean;
    disabled?: boolean;
    readonly?: boolean;
    dense?: boolean;
    outlined?: boolean;
    appendIcon?: string;
    messages?: string | string[];
    errorMessages?: string | string[];
    small?: boolean;
    rules?: Array<(value: T | null) => true | string>;
    tabindex?: string;
  }>(),
  {
    type: 'integer' as never, // Vue 2.7 can't infer it
    precision: undefined,
    rounding: Decimal.ROUND_DOWN,
    min: 0,
    max: undefined,
    step: 1,
    label: undefined,
    placeholder: undefined,
    hint: undefined,
    prefix: undefined,
    suffix: undefined,
    autofocus: undefined,
    clearable: undefined,
    disabled: undefined,
    readonly: undefined,
    dense: undefined,
    outlined: undefined,
    appendIcon: undefined,
    messages: undefined,
    errorMessages: undefined,
    small: undefined,
    rules: () => [],
    tabindex: undefined,
  }
);

const emit = defineEmits<{
  (event: 'blur' | 'focus'): void;
  (event: 'input' | 'change', value: T | null): void;
}>();

const rInput = shallowRef<null | HTMLInputElement>(null);

/**
 * Inject defaults and return
 */
const precision = computed<{ min: number; max: number }>(() => {
  if (typeof props.precision === 'undefined' || props.type === 'integer') {
    // default decimal precision allows a range of decimal points
    return {
      min: 0,
      max: props.type === 'decimal' ? DEFAULT_DECIMAL_MAX_PRECISION : DEFAULT_INTEGER_PRECISION,
    };
  }

  if (typeof props.precision === 'object') {
    return {
      min: props.precision.min ?? 0,
      max: props.precision.max ?? DEFAULT_DECIMAL_MAX_PRECISION,
    };
  }

  return { min: props.precision, max: props.precision };
});

const cssClass = computed(() => (props.small ? 'small' : ''));

const {
  stringValue,
  formattedInputValue,
  isTogglingDomSync,
  dropBackup,
  thousandSeparator,
  decimalSeparator,
} = useLocaleString('en-US', () => ({
  notation: 'standard',
  minimumFractionDigits: precision.value.min,
  maximumFractionDigits: precision.value.max,
  roundingPriority: 'lessPrecision',
}));
const inputValue = computed({
  get() {
    if (stringValue.value === null) return null;
    return new Decimal(stringValue.value);
  },
  set(v) {
    const stringValueWas = stringValue.value;
    stringValue.value = v === null ? '' : v.toFixed(precision.value.max);
    if (stringValueWas !== stringValue.value) {
      emitValue('input');
    }
  },
});

watch(
  () =>
    props.value === null || props.value instanceof Decimal ? props.value : new Decimal(props.value),
  (newValue: Decimal | null, oldValue?: Decimal | null) => {
    // only call setValue if the new value is different from old
    if (newValue === oldValue || (newValue && oldValue && newValue.equals(oldValue))) {
      return;
    }

    // do rounding if newValue is not null
    if (newValue !== null) {
      newValue = new Decimal(newValue).toDecimalPlaces(precision.value.max, props.rounding);
    }
    inputValue.value = newValue;
  },
  { flush: 'sync', immediate: true }
);

onBeforeMount(() => {
  if (typeof props.value === 'undefined') {
    throw new Error(
      'Component requires a v-model of type null | number | Decimal. Did you forget to pass it?'
    );
  }
});

/*
 * Emit numeric value to the parent component
 * Use type to decide if it should emit a Decimal or a number
 */
function emitValue(eventName: 'input' | 'change'): void {
  emit(
    eventName,
    (props.type === 'decimal'
      ? inputValue.value
      : (inputValue.value?.toNumber() ?? null)) as T | null
  );
}

/*
 * Trigger step value modifications via the keyboard
 * preventDefault avoids cursor moving to start/end of line
 */
function onKeyDown(ev: KeyboardEvent): void {
  if (props.readonly || props.disabled) return;
  if (ev.key === 'ArrowUp') {
    ev.preventDefault();
    addToValue(props.step);
  }
  if (ev.key === 'ArrowDown') {
    ev.preventDefault();
    addToValue(-props.step);
  }
}

/*
 * Add (or subtract) a value
 */
function addToValue(diff: number): void {
  // Add diff to currentValue
  let newValue = inputValue.value === null ? new Decimal(diff) : inputValue.value.add(diff);

  if (newValue.lt(props.min)) {
    newValue = new Decimal(props.min);
  } else if (props.max !== undefined && newValue.gt(props.max)) {
    newValue = new Decimal(props.max);
  }

  // when decreasing, we first try to round down to the nearest step;
  // not needed when increasing because we want the user to reach max
  // (even it it's not a multiple of step)
  if (diff < 0) {
    // round to the nearest step
    newValue = new Decimal(newValue).toNearest(props.step, Decimal.ROUND_CEIL);
  }

  inputValue.value = newValue;
}

// don't accept decimals for integer field on paste
function onPaste(event: ClipboardEvent) {
  event.preventDefault();
  const paste = event.clipboardData!.getData('text').trim();
  const decimalPlace = paste.indexOf(decimalSeparator.value);
  formattedInputValue.value =
    props.type === 'integer' && decimalPlace > -1 && Number(paste.slice(decimalPlace + 1))
      ? ''
      : paste;
}

function getNativeInput(): HTMLInputElement | null {
  // rInput holds the VTextField component instance (despite the HTMLInputElement typing);
  // traverse to the actual native <input> element
  const component = rInput.value as unknown as { $el?: HTMLElement } | null;
  return component?.$el?.querySelector('input') ?? null;
}

/**
 * Handle text input from the v-text-field, preserving cursor position across formatting.
 *
 * v-model has already run by the time this handler fires, so the formatted value is computed.
 * We read the cursor position from the native input (still showing the user's raw keystroke),
 * then after Vue re-renders with the formatted string, we restore the cursor to the
 * equivalent position by counting non-separator characters.
 */
function onTextInput() {
  const inputEl = getNativeInput();
  if (inputEl) {
    const cursorPos = inputEl.selectionStart;
    if (cursorPos !== null) {
      const sep = thousandSeparator.value;
      const meaningfulCount = countMeaningfulChars(inputEl.value, cursorPos, sep);
      nextTick(() => {
        const formatted = formattedInputValue.value;
        if (inputEl.value !== formatted) {
          inputEl.value = formatted;
        }
        const newPos = findPositionAfterNMeaningful(inputEl.value, meaningfulCount, sep);
        inputEl.selectionStart = inputEl.selectionEnd = newPos;
      });
    }
  }
  // Skip emit when the null toggle for DOM sync is in progress — the numeric
  // value hasn't actually changed, and inputValue would incorrectly read as null.
  if (!isTogglingDomSync.value) {
    emitValue('input');
  }
}
</script>

<!--
  Wraps <v-text-field> enabling masked numeric input.
  Automatically adds decimals and thousands separators as the user types.

  Expects a v-model value of type number (integer) or Decimal.

  Property "type" dictates how v-model bahaves:

    - type="integer" (default value)
      - emits and expects a type "number" (integer)
      - precision is always 0 (prop is ignored if passed when integer)

    - type="decimal"
      - emits and expects a type "Decimal"
      - precision can be a fixed value or a range (default is {max: 16}):
          - :precison="2"                   -> always 2 decimal places
          - :precision="{min: 4}"           -> between 4 and 16 decimal places
          - :precision="{min: 2, max: 4}"   -> between 2 and 4 decimal places
          - :precision="{max: 16}"          -> between 0 and 16 decimal places

  Renders an input of type="text" (because of the decimal and thousands separators).
  Accepts "step" and "min" properties, mimicking type="number" behavior.
-->
<template>
  <v-text-field
    :ref="(ref) => (rInput = ref)"
    v-model="formattedInputValue"
    :append-icon="appendIcon"
    :autofocus="autofocus"
    :class="cssClass"
    :clearable="clearable"
    data-test="numeric-input"
    :dense="dense"
    :disabled="disabled"
    :error-messages="errorMessages"
    :hint="hint"
    :label="label"
    :max="max"
    :messages="messages"
    :min="min"
    :outlined="outlined"
    :placeholder="placeholder"
    :prefix="prefix"
    :readonly="readonly"
    :rules="rules"
    :step="step"
    :suffix="suffix"
    :tabindex="tabindex"
    type="text"
    @blur="
      emit('blur');
      dropBackup();
    "
    @change="emitValue('change')"
    @focus="emit('focus')"
    @input="onTextInput"
    @keydown="onKeyDown"
    @paste="onPaste"
  >
    <!-- Forward slots -->
    <template v-for="(_index, name) in $slots" #[name]>
      <slot :name="name" />
    </template>
  </v-text-field>
</template>
